import React, { useMemo, useState, useEffect } from "react";
import { MagnifyingGlass, CheckCircle, Plus } from "@phosphor-icons/react";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";

/**
 * RecipePickerDialog — bulk picker for My Recipes.
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - recipes: array of {id, dish_name, category, summary}
 *  - onAdd: (selectedRecipes[]) => void  — invoked with array of full recipe objects
 *  - defaultCategory: optional string to pre-fill the menu-section field
 */
export default function RecipePickerDialog({ open, onClose, recipes, onAdd, defaultCategory = "" }) {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("Any");
  const [selected, setSelected] = useState(new Set());

  useEffect(() => {
    if (open) { setQ(""); setCat("Any"); setSelected(new Set()); }
  }, [open]);

  const categories = useMemo(() => {
    const s = new Set();
    (recipes || []).forEach((r) => { if (r.category) s.add(r.category); });
    return ["Any", ...Array.from(s).sort()];
  }, [recipes]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return (recipes || []).filter((r) => {
      if (cat !== "Any" && (r.category || "") !== cat) return false;
      if (!needle) return true;
      return (r.dish_name || "").toLowerCase().includes(needle) ||
             (r.category || "").toLowerCase().includes(needle);
    });
  }, [recipes, q, cat]);

  const toggle = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(filtered.map((r) => r.id)));
  const clearAll = () => setSelected(new Set());

  const confirm = () => {
    const chosen = (recipes || []).filter((r) => selected.has(r.id));
    if (!chosen.length) return;
    onAdd(chosen, defaultCategory);
    onClose?.();
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="recipe-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[#0A3B24]">
            Browse My Recipes
          </DialogTitle>
          <p className="text-xs text-[#4B5563]">
            Pick one or more recipes to add to your menu. Food cost per portion is auto-pulled from each recipe.
          </p>
        </DialogHeader>

        {/* Filter bar */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-7">
            <Label className="kostra-data-label mb-1.5 block">Search</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5563]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by dish name or category…"
                className="h-11 pl-9"
                data-testid="picker-search-input"
              />
            </div>
          </div>
          <div className="md:col-span-5">
            <Label className="kostra-data-label mb-1.5 block">Category</Label>
            <div className="flex flex-wrap gap-1.5" data-testid="picker-category-chips">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCat(c)}
                  className={`text-xs px-2.5 py-1.5 rounded-full border-2 font-semibold transition ${
                    cat === c
                      ? "bg-[#0A3B24] text-white border-[#0A3B24]"
                      : "bg-white text-[#0A3B24] border-[#0A3B24]/30 hover:border-[#0A3B24]"}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Selection helpers */}
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#4B5563]">
            {filtered.length} recipe{filtered.length === 1 ? "" : "s"} ·{" "}
            <span className="font-semibold text-[#0A3B24]">{selected.size} selected</span>
          </span>
          <div className="flex gap-2">
            <button type="button" onClick={selectAll}
                    className="text-xs font-semibold text-[#0A3B24] hover:underline">Select all</button>
            <span className="text-[#E5E7EB]">·</span>
            <button type="button" onClick={clearAll}
                    className="text-xs font-semibold text-[#4B5563] hover:underline">Clear</button>
          </div>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-h-[55vh] overflow-y-auto pr-1"
             data-testid="picker-grid">
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-xs text-[#4B5563] py-8 px-4 rounded-md border border-dashed border-[#E5E7EB] bg-[#F9FAFB]">
              No recipes match your filters.
            </div>
          )}
          {filtered.map((r) => {
            const isOn = selected.has(r.id);
            const cost = Number(r.summary?.cost_per_portion || 0);
            return (
              <button
                key={r.id}
                type="button"
                onClick={() => toggle(r.id)}
                className={`text-left rounded-xl border-2 p-3 transition relative ${
                  isOn
                    ? "border-[#D4AF37] bg-[#FDF9EE]"
                    : "border-[#E5E7EB] bg-white hover:border-[#0A3B24]/30"}`}
                data-testid={`picker-card-${r.id}`}
              >
                {isOn && (
                  <CheckCircle size={20} weight="fill" className="absolute top-2 right-2 text-[#0A3B24]" />
                )}
                <div className="font-display text-sm text-[#0A3B24] leading-tight pr-6">
                  {r.dish_name}
                </div>
                {r.category && (
                  <div className="mt-1 inline-block text-[10px] uppercase tracking-wider text-[#A87C0E] font-bold border border-[#D4AF37]/40 bg-[#FDF9EE] px-1.5 py-0.5 rounded">
                    {r.category}
                  </div>
                )}
                <div className="mt-2 text-[11px] text-[#4B5563]">
                  Food Cost / portion
                </div>
                <div className="font-mono-num font-bold text-[#0A3B24] text-sm">
                  ₹{cost.toFixed(2)}
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 -mx-6 -mb-6 px-6 py-4 bg-white border-t border-[#E5E7EB] flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}
                  className="border-[#0A3B24]/30 text-[#0A3B24] hover:bg-[#FDF9EE] font-semibold">
            Cancel
          </Button>
          <Button onClick={confirm} disabled={!selected.size}
                  className="bg-[#0A3B24] hover:bg-[#0E5232] text-white font-semibold disabled:opacity-50"
                  data-testid="picker-add-btn">
            <Plus size={14} weight="bold" className="mr-1.5" />
            Add {selected.size > 0 ? `${selected.size} ` : ""}to Menu
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
